import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { Controller, useForm } from 'react-hook-form';
import { $cardsListStore, $initialAccountsStore, $profileFeatureStore } from '@lib/store';
import { AccountUtils, ValidateUtils } from '@shared/utils';
import { type CreateUserRequest, UserType } from '@shared/api/rest/users';
import { AccountType } from '@shared/api/rest/accounts';
import { RadioInputOrientationTypeEnum } from '@shared/widgets/inputs/radio/models';
import { SelectUserAccountsAndCards } from './selectUserAccountsAndCards';
import { BaseInput, Button, ErrorMessage, Modal, PhoneInput, RadioInput } from '@shared/widgets';
import { Constants } from '@shared/constants';

const USER_TYPE_OPTIONS = [
  { value: UserType.MANAGER, text: UserType.MANAGER },
  { value: UserType.USER, text: UserType.USER },
];

interface IForm {
  userName: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  position: string;
  phone: { countryCode: string; phoneNumber: string };
  userType: UserType;
  accounts: number[];
  cards: number[];
}
const DEFAULT_VALUES: IForm = {
  userName: '',
  firstName: '',
  lastName: '',
  emailAddress: '',
  position: '',
  phone: { countryCode: Constants.COUNTRY_CODE, phoneNumber: '' },
  userType: UserType.USER,
  accounts: [],
  cards: [],
};

interface Props {
  show: boolean;
  onClose: () => void;
  addNewUser: (arg: CreateUserRequest) => Promise<void>;
}

export const AddNewUser = (props: Props) => {
  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const userAccountsStore = useUnit($initialAccountsStore.store);
  const userCardsStore = useUnit($cardsListStore.store);

  const [selectedAccount, setSelectedAccount] = useState<number | undefined>();

  const { control, formState, handleSubmit, watch, setValue, reset } = useForm<IForm>({ defaultValues: DEFAULT_VALUES });
  const userType = watch('userType');

  useEffect(() => {
    if (!selectedAccount) return;

    $cardsListStore.events.getUserCards({ AccountId: selectedAccount, Count: 100 });
  }, [selectedAccount]);

  const closeHandler = () => {
    props.onClose();
    reset(DEFAULT_VALUES);
  };
  const submitHandler = async (data: IForm) => {
    await props.addNewUser({
      userName: data.userName,
      firstName: data.firstName,
      lastName: data.lastName,
      emailAddress: data.emailAddress,
      position: data.position,
      phoneAreaCode: data.phone.countryCode,
      phoneNumber: data.phone.phoneNumber,
      userType: data.userType,
      accountId: data.accounts,
      cardId: data.cards,
    });
    closeHandler();
  };

  const accountList = useMemo(() => {
    if (userAccountsStore.loading || !profileFeatureStore.data?.accountFlows) return [];
    return AccountUtils.filterAccounts(profileFeatureStore.data.accountFlows, userAccountsStore.data);
  }, [userAccountsStore.loading, profileFeatureStore.data?.accountFlows]);
  const accountCardsList = useMemo(
    () => (accountList.length > 0 ? accountList.filter((account) => account.type === AccountType.Card) : []),
    [accountList],
  );
  return (
    <Modal isOpen={props.show} onClose={closeHandler} title='Add new user'>
      <form className='flex flex-col gap-5' onSubmit={handleSubmit(submitHandler)} noValidate>
        <Controller
          name='userName'
          control={control}
          rules={{
            required: ValidateUtils.requiredErrorMessage,
            minLength: { value: 8, message: 'Minimum 8 characters' },
            pattern: { value: ValidateUtils.noSpacesRegex, message: ValidateUtils.noSpacesErrorMessage },
          }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} placeholder='Username' />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='firstName'
          control={control}
          rules={{ required: ValidateUtils.requiredErrorMessage }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} placeholder='First name' />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='lastName'
          control={control}
          rules={{ required: ValidateUtils.requiredErrorMessage }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} placeholder='Last name' />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='emailAddress'
          control={control}
          rules={{
            required: ValidateUtils.requiredErrorMessage,
            pattern: { value: ValidateUtils.emailRegex, message: ValidateUtils.emailErrorMessage },
          }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} type='email' placeholder='E-mail' />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='position'
          control={control}
          rules={{ required: ValidateUtils.requiredErrorMessage }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} placeholder='Position / Role' />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='phone'
          control={control}
          rules={{
            required: ValidateUtils.requiredErrorMessage,
            validate: (value) => {
              if (!value?.countryCode) return ValidateUtils.requiredErrorMessage;
              if (!value?.phoneNumber) return ValidateUtils.requiredErrorMessage;
              if (!ValidateUtils.validatePhoneNumber(value.phoneNumber)) return ValidateUtils.phoneErrorMessage;
              return true;
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <PhoneInput enableSearch value={field.value} onChange={field.onChange} />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='userType'
          control={control}
          render={({ field }) => (
            <RadioInput
              orientation={RadioInputOrientationTypeEnum.Horizontal}
              options={USER_TYPE_OPTIONS}
              selected={field.value}
              onChange={(e) => {
                field.onChange(e);
                if (userType === UserType.USER) {
                  setSelectedAccount(undefined);
                  setValue('cards', []);
                } else {
                  setValue('accounts', []);
                }
              }}
            />
          )}
        />
        {userType === UserType.MANAGER && (
          <Controller
            name='accounts'
            control={control}
            rules={{ required: ValidateUtils.requiredErrorMessage }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <SelectUserAccountsAndCards
                  userType={userType}
                  accountLoading={userAccountsStore.loading}
                  accountList={accountList}
                  selectedAccounts={field.value}
                  changeAccountOrCardId={field.onChange}
                />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
        )}
        {userType === UserType.USER && (
          <Controller
            name='cards'
            control={control}
            rules={{ required: ValidateUtils.requiredErrorMessage }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <SelectUserAccountsAndCards
                  userType={userType}
                  accountLoading={userAccountsStore.loading}
                  accountList={accountCardsList}
                  selectedAccount={selectedAccount}
                  setSelectedAccount={setSelectedAccount}
                  selectedCardValue={selectedAccount}
                  cardList={userCardsStore.data}
                  cardLoading={userCardsStore.loading}
                  selectedCards={field.value}
                  changeAccountOrCardId={field.onChange}
                />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
        )}
        <Button className='mt-7' fullWidth color='primary' type='submit' disabled={formState.isSubmitting}>
          Save changes
        </Button>
      </form>
    </Modal>
  );
};
